import { useMemo, useRef, useState } from 'react';

import { type RecentSearches, readSearchData, type SearchDataBatch, type SearchRedirectResponse } from '../lib';
import type { ProductSearchItem, SearchRedirect, SearchSource } from '../search.types';

type SearchSnapshot = {
    applicationRedirects: readonly SearchRedirect[];
    loadedProductShortcut: ProductSearchItem | undefined;
    query: string | undefined;
    sources: readonly SearchSource[];
};

const EMPTY_SNAPSHOT: SearchSnapshot = {
    applicationRedirects: [],
    loadedProductShortcut: undefined,
    query: undefined,
    sources: [],
};

type ResolvedSearchRequest = {
    batch: SearchDataBatch;
    loadedProductShortcut: ProductSearchItem | undefined;
    query: string;
    redirects: readonly SearchRedirect[];
    sources: readonly SearchSource[];
};

type CoreRedirectContext = {
    query: string;
    redirects: readonly SearchRedirect[];
};

type UseSearchResultsOptions = {
    analyticsTags: string[];
    clickAnalytics: boolean;
    query: string;
    recentSearches: RecentSearches | null;
    searchDebounceMs: number;
};

function createDebouncedRead(delay: number) {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let settleSuperseded: (() => void) | undefined;

    return (read: () => Promise<SearchDataBatch>) => {
        if (timer) {
            clearTimeout(timer);
            settleSuperseded?.();
        }
        if (!delay) return read();

        return new Promise<SearchDataBatch | undefined>((resolve, reject) => {
            settleSuperseded = () => resolve(undefined);
            timer = setTimeout(() => {
                timer = undefined;
                settleSuperseded = undefined;
                Promise.resolve().then(read).then(resolve, reject);
            }, delay);
        });
    };
}

function createConcurrentSafeRead() {
    let requestId = -1;
    let latestResolvedId = -1;
    let latestResolvedValue: ResolvedSearchRequest | undefined;

    return (request: Promise<ResolvedSearchRequest | undefined>) => {
        requestId += 1;
        const currentRequestId = requestId;

        return request.then(value => {
            if (!value) return undefined;
            if (latestResolvedValue && currentRequestId < latestResolvedId) {
                return latestResolvedValue;
            }

            latestResolvedId = currentRequestId;
            latestResolvedValue = value;
            return value;
        });
    };
}

function updateRedirects(
    currentRedirects: readonly SearchRedirect[],
    responses: readonly SearchRedirectResponse[],
    currentQuery: string
): SearchRedirect[] {
    const redirects = currentRedirects.map(redirect => ({ ...redirect }));

    for (const response of responses) {
        if (response.query !== currentQuery) continue;

        const existingIndex = redirects.findIndex(redirect => redirect.sourceId === response.sourceId);

        if (response.url === undefined) {
            if (existingIndex !== -1) redirects.splice(existingIndex, 1);
            continue;
        }

        const redirect: SearchRedirect = {
            sourceId: response.sourceId,
            urls: [response.url],
        };

        if (existingIndex === -1) {
            redirects.push(redirect);
        } else {
            redirects[existingIndex] = redirect;
        }
    }

    return redirects;
}

function createSnapshotItems(batch: SearchDataBatch, redirects: readonly SearchRedirect[], currentCoreQuery: string) {
    const queryHasRedirect = redirects.some(redirect => redirect.sourceId === 'querySuggestionsPlugin');
    const isRedirectedQuery = (itemQuery: string) =>
        queryHasRedirect && itemQuery.toLowerCase() === currentCoreQuery.toLowerCase();
    const queryItems = queryHasRedirect
        ? batch.queryItems.filter(item => !isRedirectedQuery(item.label))
        : batch.queryItems;
    const queryCollectionItemCount = batch.queryCollectionLabels.filter(
        itemQuery => !isRedirectedQuery(itemQuery)
    ).length;
    const productPositionStart = redirects.length + batch.recentItems.length + queryCollectionItemCount;
    const products = batch.productItems.map((product, index) => ({
        ...product,
        legacyPosition: productPositionStart + index,
    }));
    const loadedProductShortcut = products.length === 1 ? products[0] : undefined;

    const sources: SearchSource[] = [
        {
            kind: 'recent' as const,
            items: batch.recentItems,
        },
        {
            kind: 'query' as const,
            items: queryItems,
        },
        {
            kind: 'product' as const,
            items: products,
        },
    ].filter(source => source.items.length > 0);

    return { loadedProductShortcut, sources };
}

export function useSearchResults({
    analyticsTags,
    clickAnalytics,
    query,
    recentSearches,
    searchDebounceMs,
}: UseSearchResultsOptions) {
    const [snapshot, setSnapshot] = useState<SearchSnapshot>(EMPTY_SNAPSHOT);
    const snapshotRef = useRef(snapshot);
    const currentCoreContextRef = useRef<CoreRedirectContext>({ query, redirects: [] });
    currentCoreContextRef.current = { query, redirects: [] };
    const debouncedRead = useMemo(() => createDebouncedRead(searchDebounceMs), [searchDebounceMs]);
    const concurrentSafeRead = useMemo(createConcurrentSafeRead, []);

    const reload = (nextQuery: string) => {
        const recentItems = recentSearches?.list(nextQuery) ?? [];
        const requestCoreContext = currentCoreContextRef.current;
        requestCoreContext.query = nextQuery;

        const request = debouncedRead(() =>
            readSearchData({
                analyticsTags,
                clickAnalytics,
                query: nextQuery,
                recentItems,
            })
        ).then(batch => {
            if (!batch) return undefined;
            requestCoreContext.redirects = updateRedirects(
                requestCoreContext.redirects,
                batch.redirectResponses,
                requestCoreContext.query
            );
            const { loadedProductShortcut, sources } = createSnapshotItems(
                batch,
                requestCoreContext.redirects,
                requestCoreContext.query
            );

            return {
                batch,
                loadedProductShortcut,
                query: nextQuery,
                redirects: requestCoreContext.redirects,
                sources,
            };
        });

        return concurrentSafeRead(request)
            .then(resolved => {
                if (!resolved) return false;

                const nextSnapshot = {
                    applicationRedirects: resolved.redirects,
                    loadedProductShortcut: resolved.loadedProductShortcut,
                    query: resolved.query,
                    sources: resolved.sources,
                };
                snapshotRef.current = nextSnapshot;
                setSnapshot(nextSnapshot);

                return true;
            })
            .catch(() => false);
    };

    const refresh = (nextQuery: string) => {
        void reload(nextQuery);
    };

    const getCurrentCoreQuery = () => currentCoreContextRef.current.query;
    const getCurrentRedirects = () => currentCoreContextRef.current.redirects;
    const getApplicationRedirects = (currentQuery: string) =>
        snapshotRef.current.query === currentQuery ? snapshotRef.current.applicationRedirects : [];
    const getLoadedProductShortcut = (currentQuery: string) =>
        snapshotRef.current.query === currentQuery ? snapshotRef.current.loadedProductShortcut : undefined;

    const resetSnapshot = () => {
        snapshotRef.current = EMPTY_SNAPSHOT;
        setSnapshot(EMPTY_SNAPSHOT);
    };

    return {
        ...snapshot,
        getApplicationRedirects,
        getCurrentCoreQuery,
        getCurrentRedirects,
        getLoadedProductShortcut,
        hasVisibleResults: snapshot.sources.some(source => source.items.length > 0),
        refresh,
        reload,
        resetSnapshot,
    };
}
